import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, FolderTree, ArrowLeft, Truck, Calendar, CalendarRange, Settings, Users, Crown, MapPin, Layers, Mail, PackageCheck, BarChart3, FileText, ShieldCheck, ClipboardList, Menu, X, BookOpen, GraduationCap, MessageCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const dashboardLink = { href: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true };

// Grouped navigation — group headings render in the gold accent to clearly
// separate them from their sub-items.
const navGroups = [
  { heading: 'Operations', items: [
    { href: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { href: '/admin/comms', label: 'WhatsApp Delivery', icon: MessageCircle },
    { href: '/admin/windows', label: 'Order Windows', icon: Calendar },
    { href: '/admin/order-cycle', label: 'Order Cycle', icon: CalendarRange },
    { href: '/admin/packing', label: 'Packing List', icon: PackageCheck },
    { href: '/admin/collection-points', label: 'Collection Points', icon: MapPin },
  ] },
  { heading: 'Inventory', items: [
    { href: '/admin/products', label: 'Products', icon: Package },
    { href: '/admin/main-categories', label: 'Main Categories', icon: Layers },
    { href: '/admin/categories', label: 'Categories', icon: FolderTree },
  ] },
  { heading: 'Procurement', items: [
    { href: '/admin/suppliers', label: 'Suppliers', icon: Truck },
    { href: '/admin/purchase-orders', label: 'Purchase Orders', icon: ClipboardList },
  ] },
  { heading: 'Customers', items: [
    { href: '/admin/users', label: 'Customers', icon: Users },
  ] },
  { heading: 'Marketing & Content', items: [
    { href: '/admin/about', label: 'About Page', icon: FileText },
    { href: '/admin/email-templates', label: 'Email Templates', icon: Mail },
  ] },
  { heading: 'Insights', items: [
    { href: '/admin/sales-reports', label: 'Sales Reports', icon: BarChart3 },
    { href: '/admin/supplier-reports', label: 'Supplier Reports', icon: Truck },
    { href: '/admin/audit-log', label: 'Audit Log', icon: ShieldCheck },
  ] },
  { heading: 'Administration', items: [
    { href: '/admin/settings', label: 'Settings', icon: Settings },
  ] },
];

export default function AdminLayout() {
  const { user, isAdmin, loading, refreshUser } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // DEFENCE IN DEPTH: re-verify the user's role with the server on every
  // admin page navigation. A JWT carries a snapshot of role at issuance time,
  // so if an admin demotes a user mid-session, the stale JWT could still
  // pass through `isAdmin` on the next admin navigation. This re-pulls
  // /api/auth/me which reads role fresh from the DB. Backend `get_admin_user`
  // already rejects non-admins with 403, but this hides the UI shell too.
  useEffect(() => {
    if (user && refreshUser) {
      refreshUser().catch(() => { /* AuthContext nulls user on 401 */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="font-body text-foreground/70">Loading...</p>
    </div>
  );
  if (!user || !isAdmin) return <Navigate to="/auth" replace />;

  const isActive = (href, exact) =>
    exact ? location.pathname === href : location.pathname.startsWith(href);

  const renderLink = (link) => {
    const active = isActive(link.href, link.exact);
    return (
      <Link
        key={link.href}
        to={link.href}
        data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-body text-sm transition-colors ${
          active
            ? 'bg-vintage text-white'
            : 'text-foreground/60 hover:bg-muted hover:text-foreground'
        }`}
      >
        <link.icon className="h-[18px] w-[18px] flex-shrink-0" />
        {link.label}
      </Link>
    );
  };

  return (
    <div data-testid="admin-layout" className="min-h-screen lg:flex bg-background">
      {/* Mobile top bar (hidden on desktop) */}
      <div
        className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 pb-3 bg-card border-b border-border"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
        data-testid="admin-mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          data-testid="admin-sidebar-open-btn"
          aria-label="Open admin menu"
          className="p-2 -ml-2 rounded-lg text-foreground/70 hover:bg-muted hover:text-foreground transition-colors"
        >
          <Menu className="h-6 w-6" />
        </button>
        <Link to="/admin" aria-label="Admin dashboard">
          <img src="/logo.png" alt="AVZ Wholesale" className="h-8 w-auto logo-colored" />
        </Link>
        <div className="w-10" />
      </div>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-backdrop"
        />
      )}

      {/* Sidebar — off-canvas drawer on mobile, static column on desktop */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 max-w-[85vw] bg-card border-r border-border px-6 pb-6 flex flex-col transform transition-transform duration-300 ease-in-out lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1.5rem)' }}
        data-testid="admin-sidebar"
      >
        {/* Close button (mobile only) */}
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-close-btn"
          aria-label="Close admin menu"
          className="lg:hidden absolute right-4 z-10 p-2 rounded-lg text-foreground/60 hover:bg-muted hover:text-foreground transition-colors"
          style={{ top: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex flex-col items-center mb-6">
          <Link to="/" data-testid="admin-logo-home" aria-label="Go to home page">
            <img src="/logo.png" alt="AVZ Wholesale" className="w-[200px] max-w-full h-auto logo-colored mb-3" />
          </Link>
          <div className="flex items-center gap-2">
            <Crown className="h-3.5 w-3.5 text-gold" />
            <span className="font-body text-xs text-foreground/70 font-medium">{user?.name || 'Admin'}</span>
          </div>
          <span className="font-body text-[10px] text-foreground/40 uppercase tracking-widest mt-0.5">Administrator</span>
          <Link
            to="/"
            data-testid="admin-back-to-shop"
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-gold text-charcoal font-body text-xs font-bold uppercase tracking-wider hover:bg-gold/80 transition-all shadow-md hover:shadow-lg"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Shop
          </Link>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto -mr-3 pr-3">
          {renderLink(dashboardLink)}
          {navGroups.map(group => (
            <div key={group.heading} className="pt-4" data-testid={`admin-nav-group-${group.heading.toLowerCase().replace(/[\s&]+/g, '-')}`}>
              <div className="flex items-center gap-2 px-4 pb-1.5">
                <span className="h-px w-3 bg-gold/70" />
                <span className="font-body text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
                  {group.heading}
                </span>
              </div>
              <div className="space-y-0.5">
                {group.items.map(renderLink)}
              </div>
            </div>
          ))}
          {/* Help & Guides — authenticated PDF downloads (open in a new tab; the
              admin session cookie is sent automatically to the /api gateway). */}
          <div className="pt-4" data-testid="admin-nav-group-help">
            <div className="flex items-center gap-2 px-4 pb-1.5">
              <span className="h-px w-3 bg-gold/70" />
              <span className="font-body text-[10px] font-bold uppercase tracking-[0.18em] text-gold">Help &amp; Guides</span>
            </div>
            <div className="space-y-0.5">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/guide`}
                target="_blank" rel="noopener noreferrer"
                data-testid="admin-guide-link"
                className="flex items-center gap-3 px-4 py-2 rounded-lg font-body text-sm text-foreground/70 hover:bg-muted hover:text-foreground transition-colors"
              >
                <BookOpen className="h-4 w-4" /> Admin Guide (PDF)
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/guide/onboarding`}
                target="_blank" rel="noopener noreferrer"
                data-testid="admin-onboarding-guide-link"
                className="flex items-center gap-3 px-4 py-2 rounded-lg font-body text-sm text-foreground/70 hover:bg-muted hover:text-foreground transition-colors"
              >
                <GraduationCap className="h-4 w-4" /> Onboarding Guide (PDF)
              </a>
            </div>
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
